import type { LinkSlots } from "../detail/linkSlots";
import { fristZurTwv } from "../shared/fristZurTwv";

/**
 * Der Entwurf eines neuen To-dos, wie ihn der Anlege-Kasten in der
 * Vorgangskarte hält (PO 05.09.2026, Zeichnung: Betreff, Zuweisung, Fristen,
 * Beteiligte und zu Informierende schon BEIM Anlegen).
 *
 * Vorher nahm die Detailansicht nur einen Betreff entgegen; alles Weitere
 * musste man danach am aufgeklappten To-do nachtragen. Die Anlage-Maske
 * (`TodoDraftCard`) fragt seit jeher nach allem — die beiden Wege fragten
 * dasselbe verschieden vollständig ab.
 */
export type TodoEntwurf = {
    titel: string;
    slots: LinkSlots;
    /** 'YYYY-MM-DD' oder null. */
    twv: string | null;
    frist: string | null;
    /** Nur UI-Zustand: darf eine TWV-Änderung die Frist weiter mitführen? */
    fristAutomatisch: boolean;
    hauptverantwortlich: string[];
    bearbeiter: string[];
    zuInformierende: string[];
};

/**
 * Deckel für Beteiligten-Listen — Spiegel von `MAX_BEARBEITER`
 * (`apps/api/src/vorgang/note-bearbeiter-merge.ts`). Der Server kappt ohnehin;
 * hier steht die Zahl, damit ein Entwurf gar nicht erst mehr anbietet, als
 * gespeichert würde.
 */
const MAX_BEARBEITER = 50;

/**
 * Rohe Beteiligten-Listen → eine deduplizierte Id-Liste in Reihenfolge des
 * ersten Auftretens.
 *
 * Bewusst noch einmal hier und nicht aus `utils/vorgangScope.ts:bearbeiterKeys`
 * importiert: dieses Modul kommt seit jeher mit einem einzigen `import type`
 * aus und soll das bleiben — `vorgangScope` zöge den `userService` in jeden
 * Test, der nur einen Entwurf zusammenbaut.
 */
function beteiligtenIds(...quellen: Array<unknown[] | string | null | undefined>): string[] {
    const out: string[] = [];
    for (const quelle of quellen) {
        const liste = typeof quelle === "string" ? [quelle] : (quelle ?? []);
        for (const eintrag of liste) {
            const id =
                typeof eintrag === "string"
                    ? eintrag
                    : ((eintrag as any)?.userId ?? (eintrag as any)?.id ?? (eintrag as any)?.kuerzel ?? null);
            if (id && !out.includes(id)) out.push(id);
        }
    }
    return out.slice(0, MAX_BEARBEITER);
}

/**
 * Ein frischer Entwurf. Die Zuweisung startet mit der des Vorgangs — ein To-do
 * gehört im Regelfall zu demselben Kunden, Risiko und Vertrag, und genau so
 * hat das Anlegen es bisher schon gespeichert.
 *
 * ══ DIE ROLLEN KOMMEN MIT (PO 16.09.2026) ══════════════════════════════════
 * „die todos haben ihre eigenen verantwortlichen und bearbeiter, aber
 * natürlich vorbelegt beim erstellen." Bis dahin erbte ein Entwurf nur den
 * Hauptverantwortlichen; Bearbeiter war ausschließlich der Anlegende, zu
 * Informierende blieben leer — wer im Vorgang zu dritt arbeitete, stand am
 * ersten To-do allein da und musste die beiden Kollegen von Hand nachtragen.
 *
 * Jetzt gilt: Bearbeiter = die des Vorgangs PLUS der Anlegende (er arbeitet
 * mit, sonst legte er es nicht an), zu Informierende = die des Vorgangs.
 *
 * VORBELEGUNG HEISST NICHT NACHZIEHEN: Was hier einmal eingesetzt wurde, lebt
 * am To-do weiter. Ändert sich später der Vorgang, bleiben bestehende To-dos
 * unberührt — das ist ausdrücklich so gewollt.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function leererTodoEntwurf(
    vorgangsSlots?: LinkSlots | null,
    defaults?: {
        hauptverantwortlich?: unknown[];
        /** Bearbeiter des VORGANGS — der Anlegende kommt separat dazu. */
        bearbeiter?: unknown[];
        zuInformierende?: unknown[];
        currentUserId?: string | null;
    },
): TodoEntwurf {
    // `null` ist erlaubt: das Rechtsklickmenü der Zeitachse startet einen
    // Entwurf, bevor sicher ist, dass der Vorgang schon geladen ist.
    const s = vorgangsSlots ?? { kontakt: null, risiko: null, vertrag: null, risiken: [], vertraege: [] };
    return {
        titel: "",
        slots: {
            kontakt: s.kontakt,
            kontakte: s.kontakte ? [...s.kontakte] : s.kontakt ? [s.kontakt] : [],
            risiko: s.risiko,
            vertrag: s.vertrag,
            risiken: [...s.risiken],
            vertraege: [...s.vertraege],
        },
        twv: null,
        frist: null,
        fristAutomatisch: true,
        hauptverantwortlich: beteiligtenIds(defaults?.hauptverantwortlich),
        // Der Anlegende steht hinten an: die Bearbeiter des Vorgangs behalten
        // ihre Reihenfolge, und wer ohnehin schon dabei ist, taucht nicht
        // doppelt auf.
        bearbeiter: beteiligtenIds(defaults?.bearbeiter, defaults?.currentUserId),
        zuInformierende: beteiligtenIds(defaults?.zuInformierende),
    };
}

/** TWV und eine noch automatisch geführte/leere Frist bleiben taggleich. */
export function setzeTodoTwv(entwurf: TodoEntwurf, twv: string | null): TodoEntwurf {
    return {
        ...entwurf,
        twv,
        // T-331: Eine bewusst gesetzte Frist bleibt — außer sie läge vor der TWV.
        frist: entwurf.fristAutomatisch || !entwurf.frist ? twv : fristZurTwv(twv, entwurf.frist),
    };
}

/** Eine bewusst gesetzte Frist wird von späteren TWV-Änderungen respektiert. */
export function setzeTodoFrist(entwurf: TodoEntwurf, frist: string | null): TodoEntwurf {
    return { ...entwurf, frist, fristAutomatisch: frist == null };
}

/** Nur der Betreff ist Pflicht — alles andere lässt sich nachtragen. */
export function todoEntwurfGueltig(entwurf: TodoEntwurf): boolean {
    return entwurf.titel.trim().length > 0;
}

/**
 * Der `POST /todo`-Rumpf zu einem Entwurf.
 *
 * Bewusst als reine Funktion: hier gehen Felder still verloren, wenn man sie
 * vergisst — im Erstell-Flow ist genau das schon einmal passiert (mehrfach
 * gewählte Risiken und Verträge landeten nirgends, weil nur die Singular-Slots
 * gesendet wurden). Die Arrays sind serverseitig die Quelle der Wahrheit.
 *
 * Im PERSÖNLICHEN Vorgang lehnt das Backend Bezüge und fremde Beteiligte ab
 * („In einem persönlichen Vorgang bist nur du Bearbeiter."). Die Regel steht
 * deshalb hier, nicht erst im Fehlerdialog.
 */
export function todoAnlagePayload(
    entwurf: TodoEntwurf,
    ctx: { vorgangId: number; persoenlich: boolean; currentUserId?: string | null },
): Record<string, unknown> {
    const p = ctx.persoenlich;
    return {
        titel: entwurf.titel.trim(),
        vorgangId: ctx.vorgangId,
        contactId: p ? null : (entwurf.slots.kontakt?.id ?? null),
        contactIds: p
            ? []
            : (entwurf.slots.kontakte?.length
                ? entwurf.slots.kontakte
                : entwurf.slots.kontakt
                  ? [entwurf.slots.kontakt]
                  : []
              ).map((k) => k.id),
        riskObjectId: p ? null : (entwurf.slots.risiko?.id ?? null),
        contractId: p ? null : (entwurf.slots.vertrag?.id ?? null),
        riskObjectIds: p ? [] : entwurf.slots.risiken.map((r) => r.id),
        contractIds: p ? [] : entwurf.slots.vertraege.map((v) => v.id),
        hauptverantwortlich: p ? [] : entwurf.hauptverantwortlich,
        bearbeiter: p ? (ctx.currentUserId ? [ctx.currentUserId] : []) : entwurf.bearbeiter,
        zuInformierende: p ? [] : entwurf.zuInformierende,
        twv: entwurf.twv ?? undefined,
        frist: entwurf.frist ?? undefined,
        status: "Offen",
        ersteller: ctx.currentUserId ?? undefined,
    };
}
